import { createAsyncThunk } from '@reduxjs/toolkit';
import { ipWebFetcher } from '@gjensidige/service-async';
import { remoteLogFetchError } from '@gjensidige/service-error-handler';

import {
  agentIsTyping,
  chatFinished,
  chatSessionEstablished
} from '../redux/slices/chat-state';
import { AppDispatch } from '../redux/store';
import { CallGuideSource, ChatEntryEvent } from '../types/call-guide/events';
import { boostSession } from '../utils/session';
import { chatEntry } from './callguideEventCallbackActions';
import { resetNumberOfUnreadMessages } from './unreadMessagesActions';

interface BoostMessage {
  id: string;
  type: CallGuideSource | 'client';
  msg: string;
}

type BoostEndpoint = 'get_chat_data' | 'run_post';

async function post(
  endpoint: BoostEndpoint,
  body: Record<string, string> = {}
): Promise<ChatEntryEvent[]> {
  const url = `/flaskchat/${endpoint}`;
  const res = await ipWebFetcher.postJson<{ message: BoostMessage[] }>(url, {
    body: {
      session_id: boostSession.getId(),
      ...body
    }
  });

  if (res.isOk()) {
    return res.value.message.map((m) => ({
      objectType: 'chatEntry',
      objectId: m.id,
      source: m.type === 'client' ? 'customer' : m.type,
      timeStamp: '',
      alias: '',
      message: m.msg
    }));
  }

  void remoteLogFetchError(url, res.error);
  return [];
}

const startRobotChat = createAsyncThunk<void, void, { dispatch: AppDispatch }>(
  'robotChat/start',
  async (_, { dispatch }) => {
    const messages = (await post('get_chat_data')) ?? [];

    for (const message of messages) {
      dispatch(chatEntry(message));
    }

    dispatch(chatSessionEstablished({ robot: true }));
  }
);

const write = createAsyncThunk<void, string, { dispatch: AppDispatch }>(
  'boost/write',
  async (message, { dispatch }) => {
    dispatch(
      chatEntry({
        objectType: 'chatEntry',
        objectId: '',
        source: 'customer',
        timeStamp: '',
        alias: '',
        message
      })
    );
    dispatch(agentIsTyping({ alias: 'Chatrobot', penStatus: 'on' }));

    const messages = await post('run_post', {
      message,
      msg: message,
      type: 'client',
      userAgent: navigator.userAgent
    });

    for (const message of messages) {
      dispatch(chatEntry(message));
    }

    dispatch(agentIsTyping({ alias: 'Chatrobot', penStatus: 'off' }));
  }
);

const action = createAsyncThunk<void, string, { dispatch: AppDispatch }>(
  'boost/action',
  async (actionId, { dispatch }) => {
    dispatch(agentIsTyping({ alias: 'Chatrobot', penStatus: 'on' }));

    const messages = await post('run_post', {
      action_link_id: actionId,
      type: 'client',
      id: 'link',
      userAgent: navigator.userAgent
    });

    for (const message of messages) {
      dispatch(chatEntry(message));
    }

    dispatch(agentIsTyping({ alias: 'Chatrobot', penStatus: 'off' }));
  }
);

function endRobotChat() {
  return (dispatch: AppDispatch) => {
    dispatch(chatFinished({}));
    boostSession.clear();
    setTimeout(() => {
      dispatch(resetNumberOfUnreadMessages());
    }, 1000);
  };
}

const boostActions = {
  startRobotChat,
  write,
  action,
  endRobotChat
};

export type BoostActions = typeof boostActions;

export default boostActions;
